import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Eye, Save, ArrowRight, ArrowLeft, RefreshCw,
  AlertCircle, CheckCircle, Bot, Monitor, Smartphone
} from 'lucide-react';
import { useAppStore } from '../stores/appStore';
import type { ValidationResult } from '../types';
import { clsx } from 'clsx';

// ─── Apply template variables ─────────────────────────────────────────
function applyVars(html: string, vars: Record<string, string>): string {
  let result = html;
  for (const [key, value] of Object.entries(vars)) {
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value || `{{${key}}}`);
  }
  return result;
}

// ─── Mock client-side validation ─────────────────────────────────────
function mockValidateHtml(html: string): ValidationResult {
  const warnings: string[] = [];
  const blockedItems: string[] = [];
  let xssFound = false;

  const scriptMatches = html.match(/<script[\s\S]*?<\/script>/gi);
  if (scriptMatches) {
    blockedItems.push(`${scriptMatches.length} tag <script> dikesan dan dibuang`);
    xssFound = true;
  }

  const onEventMatches = html.match(/on(click|load|error|mouseover|change|submit)=/gi);
  if (onEventMatches) {
    blockedItems.push(`${onEventMatches.length} event handler dikesan dan dibuang`);
    xssFound = true;
  }

  const httpLinks = html.match(/href="http:\/\//g);
  if (httpLinks) {
    warnings.push(`${httpLinks.length} link HTTP akan di-upgrade ke HTTPS secara automatik`);
  }

  const jsUrls = html.match(/javascript:/gi);
  if (jsUrls) {
    blockedItems.push(`${jsUrls.length} javascript: URL dibuang`);
    xssFound = true;
  }

  const iframeMatches = html.match(/<iframe[\s\S]*?>/gi);
  if (iframeMatches) {
    warnings.push(`${iframeMatches.length} iframe dikesan — pastikan ia dari sumber dipercayai`);
  }

  const sizeBytes = new Blob([html]).size;
  if (sizeBytes > 512 * 1024) {
    return {
      passed: false,
      warnings,
      blockedItems,
      securityReport: { xssFound, linksScanned: (html.match(/href=/g) || []).length, assetsScanned: (html.match(/src=/g) || []).length, finalSizeBytes: sizeBytes },
      error: `Saiz HTML terlalu besar: ${Math.round(sizeBytes / 1024)}KB (had: 512KB)`,
    };
  }

  return {
    passed: true,
    cleanHtml: html,
    warnings,
    blockedItems,
    securityReport: {
      xssFound,
      linksScanned: (html.match(/href=/g) || []).length,
      assetsScanned: (html.match(/src=/g) || []).length,
      finalSizeBytes: sizeBytes,
    },
  };
}

type ViewMode = 'split' | 'editor' | 'preview';
type DeviceView = 'desktop' | 'mobile';

export default function EditorPage() {
  const navigate = useNavigate();
  const {
    selectedTemplate, currentHtml, setCurrentHtml,
    templateVariables, setTemplateVariable,
    validationResult, setValidationResult,
    showToast,
  } = useAppStore();

  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [deviceView, setDeviceView] = useState<DeviceView>('desktop');
  const [previewHtml, setPreviewHtml] = useState('');
  const [activeTab, setActiveTab] = useState<'variables' | 'html'>('variables');
  const [isSaving, setIsSaving] = useState(false);

  // Generate preview HTML with variables applied
  useEffect(() => {
    const raw = currentHtml || selectedTemplate?.htmlContent || '';
    setPreviewHtml(applyVars(raw, templateVariables));
  }, [currentHtml, templateVariables, selectedTemplate]);

  // Run validation when HTML changes
  const runValidation = useCallback(() => {
    if (!previewHtml) return;
    const result = mockValidateHtml(previewHtml);
    setValidationResult(result);
  }, [previewHtml, setValidationResult]);

  useEffect(() => {
    const timer = setTimeout(runValidation, 800);
    return () => clearTimeout(timer);
  }, [runValidation]);

  if (!selectedTemplate && !currentHtml) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50">
        <div className="text-center">
          <div className="text-5xl mb-4">📝</div>
          <h2 className="text-xl font-bold text-surface-900 mb-2">Tiada template dipilih</h2>
          <p className="text-surface-500 mb-6">Sila pilih template atau import HTML dahulu</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate('/gallery')} className="btn-primary btn-md">
              Pilih Template
            </button>
            <button onClick={() => navigate('/import')} className="btn-outline btn-md">
              Import HTML
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    setIsSaving(true);
    // Simpan HTML dengan variables yang sudah digantikan
    setCurrentHtml(previewHtml);
    await new Promise((r) => setTimeout(r, 1000));
    setIsSaving(false);
    showToast('Draft berjaya disimpan!', 'success');
  };

  const handlePublish = () => {
    if (!validationResult?.passed) {
      showToast('Sila selesaikan isu keselamatan sebelum publish', 'error');
      return;
    }
    // PENTING: Simpan HTML dengan variables digantikan sebelum pergi ke Connect
    setCurrentHtml(previewHtml);
    navigate('/connect');
  };

  const sizeKb = Math.round((validationResult?.securityReport.finalSizeBytes || 0) / 1024);

  return (
    <div className="min-h-screen bg-surface-900 flex flex-col">
      {/* ─── Top Bar ─────────────────────────────────────────────── */}
      <div className="bg-surface-800 border-b border-surface-700 px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/gallery')}
              className="flex items-center gap-1.5 text-surface-400 hover:text-white text-sm transition-colors"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Galeri</span>
            </button>
            <div className="h-4 w-px bg-surface-700" />
            <div>
              <span className="text-white font-semibold text-sm">
                {selectedTemplate?.name || 'Import HTML'}
              </span>
              {selectedTemplate && (
                <span className="ml-2 badge bg-surface-700 text-surface-300 text-xs hidden sm:inline-flex">
                  {selectedTemplate.category}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="hidden sm:flex bg-surface-700 rounded-lg p-0.5 gap-0.5">
              {(['editor', 'split', 'preview'] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={clsx(
                    'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                    viewMode === mode
                      ? 'bg-surface-600 text-white'
                      : 'text-surface-400 hover:text-white'
                  )}
                >
                  {mode === 'editor' ? '✏️ Edit' : mode === 'split' ? '⚡ Split' : '👁️ Preview'}
                </button>
              ))}
            </div>

            {/* Device View (in preview mode) */}
            {(viewMode === 'preview' || viewMode === 'split') && (
              <div className="flex bg-surface-700 rounded-lg p-0.5 gap-0.5">
                <button
                  onClick={() => setDeviceView('desktop')}
                  className={clsx(
                    'p-1.5 rounded-md transition-all',
                    deviceView === 'desktop' ? 'bg-surface-600 text-white' : 'text-surface-400 hover:text-white'
                  )}
                >
                  <Monitor size={14} />
                </button>
                <button
                  onClick={() => setDeviceView('mobile')}
                  className={clsx(
                    'p-1.5 rounded-md transition-all',
                    deviceView === 'mobile' ? 'bg-surface-600 text-white' : 'text-surface-400 hover:text-white'
                  )}
                >
                  <Smartphone size={14} />
                </button>
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn bg-surface-700 text-surface-300 hover:bg-surface-600 hover:text-white btn-sm"
            >
              {isSaving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
              Simpan
            </button>

            <button
              onClick={handlePublish}
              className="btn-primary btn-sm"
            >
              Publish
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* ─── Main Editor Area ─────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden">

        {/* Left Panel — Editor / Variables */}
        {(viewMode === 'editor' || viewMode === 'split') && (
          <div className={clsx('flex flex-col', viewMode === 'split' ? 'w-1/2' : 'w-full')}>
            {/* Tab Bar */}
            <div className="flex bg-surface-800 border-b border-surface-700">
              <button
                onClick={() => setActiveTab('variables')}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all',
                  activeTab === 'variables'
                    ? 'border-telegram text-telegram'
                    : 'border-transparent text-surface-400 hover:text-white'
                )}
              >
                🎨 Edit Kandungan
              </button>
              <button
                onClick={() => setActiveTab('html')}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all',
                  activeTab === 'html'
                    ? 'border-telegram text-telegram'
                    : 'border-transparent text-surface-400 hover:text-white'
                )}
              >
                &lt;/&gt; Edit HTML
              </button>
            </div>

            {/* Variables Tab */}
            {activeTab === 'variables' && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {!selectedTemplate ? (
                  <div className="text-center py-10 text-surface-400">
                    <AlertCircle size={32} className="mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Tiada pemboleh ubah untuk HTML import.</p>
                    <p className="text-xs mt-1">Gunakan tab "Edit HTML" untuk edit kod secara langsung.</p>
                  </div>
                ) : (
                  selectedTemplate.variables.map((variable) => (
                    <div key={variable.key}>
                      <label className="block text-sm font-medium text-surface-300 mb-1.5">
                        {variable.label}
                        {variable.required && <span className="text-red-400 ml-1">*</span>}
                      </label>

                      {variable.type === 'textarea' ? (
                        <textarea
                          value={templateVariables[variable.key] || ''}
                          onChange={(e) => setTemplateVariable(variable.key, e.target.value)}
                          placeholder={variable.placeholder}
                          rows={3}
                          className="w-full bg-surface-700 text-white text-sm rounded-lg px-3 py-2.5 border border-surface-600 focus:outline-none focus:ring-2 focus:ring-telegram/50 focus:border-telegram resize-none placeholder-surface-500"
                        />
                      ) : variable.type === 'color' ? (
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={templateVariables[variable.key] || variable.defaultValue}
                            onChange={(e) => setTemplateVariable(variable.key, e.target.value)}
                            className="w-12 h-10 rounded-lg border border-surface-600 bg-surface-700 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={templateVariables[variable.key] || ''}
                            onChange={(e) => setTemplateVariable(variable.key, e.target.value)}
                            placeholder={variable.defaultValue}
                            className="flex-1 bg-surface-700 text-white text-sm rounded-lg px-3 py-2.5 border border-surface-600 focus:outline-none focus:ring-2 focus:ring-telegram/50 font-mono"
                          />
                        </div>
                      ) : (
                        <input
                          type={variable.type === 'url' ? 'url' : 'text'}
                          value={templateVariables[variable.key] || ''}
                          onChange={(e) => setTemplateVariable(variable.key, e.target.value)}
                          placeholder={variable.placeholder || variable.defaultValue}
                          className="w-full bg-surface-700 text-white text-sm rounded-lg px-3 py-2.5 border border-surface-600 focus:outline-none focus:ring-2 focus:ring-telegram/50 placeholder-surface-500"
                        />
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* HTML Tab */}
            {activeTab === 'html' && (
              <div className="flex-1 flex flex-col overflow-hidden">
                <textarea
                  value={currentHtml}
                  onChange={(e) => setCurrentHtml(e.target.value)}
                  className="flex-1 w-full bg-surface-900 text-green-400 font-mono text-xs p-4 resize-none focus:outline-none border-0"
                  spellCheck={false}
                  placeholder="<!-- Kod HTML anda di sini -->"
                />
              </div>
            )}

            {/* Security Report Bar */}
            {validationResult && (
              <div className={clsx(
                'border-t px-4 py-3',
                validationResult.passed
                  ? validationResult.blockedItems.length > 0
                    ? 'bg-yellow-900/30 border-yellow-700/50'
                    : 'bg-green-900/30 border-green-700/50'
                  : 'bg-red-900/30 border-red-700/50'
              )}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    {validationResult.passed ? (
                      <CheckCircle size={16} className="text-green-400" />
                    ) : (
                      <AlertCircle size={16} className="text-red-400" />
                    )}
                    <span className={clsx(
                      'font-medium',
                      validationResult.passed ? 'text-green-400' : 'text-red-400'
                    )}>
                      {validationResult.passed
                        ? validationResult.blockedItems.length > 0
                          ? `Selamat — ${validationResult.blockedItems.length} isu dibetulkan`
                          : 'HTML selamat untuk publish'
                        : validationResult.error || 'HTML tidak selamat'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-surface-400">
                    <span>{sizeKb}KB</span>
                    <span>{validationResult.securityReport.linksScanned} links</span>
                    <span>{validationResult.securityReport.assetsScanned} assets</span>
                  </div>
                </div>
                {(validationResult.warnings.length > 0 || validationResult.blockedItems.length > 0) && (
                  <div className="mt-2 space-y-1">
                    {validationResult.blockedItems.map((item, i) => (
                      <div key={i} className="text-xs text-yellow-400 flex items-center gap-1.5">
                        <span>⚠️</span> {item}
                      </div>
                    ))}
                    {validationResult.warnings.map((w, i) => (
                      <div key={i} className="text-xs text-blue-400 flex items-center gap-1.5">
                        <span>ℹ️</span> {w}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Divider */}
        {viewMode === 'split' && <div className="w-px bg-surface-700 flex-shrink-0" />}

        {/* Right Panel — Preview */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <div className={clsx(
            'flex flex-col bg-surface-950',
            viewMode === 'split' ? 'w-1/2' : 'w-full'
          )}>
            <div className="bg-surface-800 border-b border-surface-700 px-4 py-2 flex items-center gap-2">
              <Eye size={14} className="text-surface-400" />
              <span className="text-surface-400 text-xs">Live Preview</span>
              <div className="ml-auto flex items-center gap-1">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse-soft" />
                <span className="text-green-400 text-xs">Live</span>
              </div>
            </div>
            <div className="flex-1 overflow-hidden flex items-start justify-center p-4">
              <div
                className={clsx(
                  'bg-white rounded-lg overflow-hidden shadow-2xl transition-all duration-300',
                  deviceView === 'mobile' ? 'w-80' : 'w-full'
                )}
                style={{ height: 'calc(100vh - 180px)' }}
              >
                <iframe
                  srcDoc={previewHtml}
                  sandbox="allow-same-origin"
                  className="w-full h-full border-0"
                  title="Live Preview"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Bottom Bar ───────────────────────────────────────────── */}
      <div className="bg-surface-800 border-t border-surface-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="text-surface-400 text-xs">
            {selectedTemplate
              ? `Template: ${selectedTemplate.name} • ${selectedTemplate.variables.length} pemboleh ubah`
              : 'HTML Import'}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-surface-400 text-xs hidden sm:block">
              Langkah seterusnya: Terbitkan laman web anda
            </div>
            <button
              onClick={handlePublish}
              className="btn-primary btn-md"
            >
              <Bot size={16} />
              Connect Bot & Publish
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
